<template>
  <v-fade-transition mode="out-in">
    <div class="admin-wbs">
      <div class="admin-wbs--loading" v-if="isPageLoading">
        <v-progress-circular
          :width="5"
          color="grey"
          indeterminate
          :size="30"
          ></v-progress-circular>
        <div
          class="grey--text"
          :style="{
            'margin-top': '12px',
            'font-size': '14px'
          }"
          >
          Loading
        </div>
      </div>
      <v-layout wrap v-else>
        <v-flex
          xs12
          lg6
          pt-0
          pb-0
          >
          <admin-approver-card-location
            title="Finance FP&A Approver"
            :color="section_color"
            :approvers="financeApprovers"
            :loading="isFinanceApproverLoading"
            @add="startAddFinanceApprover"
            @edit="startEditFinanceApprover"
            @remove="startRemoveFinanceApprover">
          </admin-approver-card-location>
          <admin-approver-card-location
            title="Legal Approver"
            :color="section_color"
            :approvers="legalApprovers"
            :loading="isLegalApproverLoading"
            @add="startAddLegalApprover"
            @edit="startEditLegalApprover"
            @remove="startRemoveLegalApprover">
          </admin-approver-card-location>
        </v-flex>
        <v-flex
          xs12
          lg6
          pt-0
          pb-0
          >
          <admin-approver-card
            title="Finance Leader Approver"
            :color="section_color"
            :approvers="financeLeaderApprovers"
            :loading="isFinanceLeaderApproverLoading"
            @add="startAddFinanceLeaderApprover"
            @edit="startEditFinanceLeaderApprover"
            @remove="startRemoveFinanceLeaderApprover">
          </admin-approver-card>
          <admin-approver-card
            title="CPG Leader Approver"
            :color="section_color"
            :approvers="cpgLeaderApprovers"
            :loading="isCpgLeaderApproverLoading"
            @add="startAddCpgLeaderApprover"
            @edit="startEditCpgLeaderApprover"
            @remove="startRemoveCpgLeaderApprover">
          </admin-approver-card>
          <admin-approver-card
            title="CI Leader Approver"
            :color="section_color"
            :approvers="ciLeaderApprovers"
            :loading="isCiLeaderApproverLoading"
            @add="startAddCiLeaderApprover"
            @edit="startEditCiLeaderApprover"
            @remove="startRemoveCiLeaderApprover">
          </admin-approver-card>
        </v-flex>

        <confirm-dialog
          :card-style="{
            width: '450px'
          }"
          v-model="isRemovingApprover"
          :title="'Remove -- ' + edit.tableName"
          :btn-color="section_color"
          @click-yes="doRemove"
          >
          <div style="height:60px;">
            Do you confirm to remove
            <b>{{ edit.target.name }}</b>
            <template v-if="edit.industry">
              of <b>{{ edit.industry }}</b>
            </template>
            from
            <b>{{ edit.tableName }}</b>?
          </div>
        </confirm-dialog>

        <admin-edit-dialog
          :card-style="{
            width: '600px'
          }"
          v-model="isEditingApprover"
          :title="edit.typeText + ' -- ' + edit.tableName"
          :btn-color="section_color"
          :role="edit.role"
          :roles="edit.roles"
          :target="edit.target"
          :notified="edit.notified"
          :location="edit.location"
          :location-textfield="isLocationTextfieldShown"
          @click-done="saveEdit"
          >
        </admin-edit-dialog>
      </v-layout>
    </div>
  </v-fade-transition>
</template>

<script>
import { section_color } from '@/config'
import { mapActions, mapGetters } from 'vuex'

import { xPost } from '@/functions/http/axios'

import adminApproverCard from '@/components/app/admin/approver-card/default'
import adminApproverCardLocation from '@/components/app/admin/approver-card/location'
import adminEditDialog from '@/components/app/admin/edit-dialog'

import confirmDialog from '@/components/vuetify-extension/confirm-dialog'

export default {
  components: {
    adminApproverCard,
    adminApproverCardLocation,
    adminEditDialog,
    confirmDialog
  },
  data () {
    return {
      // status
      isPageLoading: true,
      isFinanceApproverLoading: true,
      isLegalApproverLoading: true,
      isFinanceLeaerApproverLoading: true,
      isCpgLeaerApproverLoading: true,
      isCiLeaderApproverLoading: true,
      isRemovingApprover: false,
      isEditingApprover: false,

      // props
      edit: {
        typeText: null,
        tableCode: null,
        tableName: null,
        location: null,
        role: null,
        roles: [],
        notified: null,
        target: {
          email: null,
          name: null,
          icon: null
        }
      },

      // data
      financeApprovers: [],
      legalApprovers: [],
      financeLeaderApprovers: [],
      cpgLeaderApprovers: [],
      ciLeaderApprovers: []
    }
  },
  computed: {
    // vuex
    ...mapGetters('user', [
      'loginStatus'
    ]),

    // params
    section_color () {
      return 'primary'
    },
    isLocationTextfieldShown () {
      return (['fin', 'lgl'].indexOf(this.edit.tableCode) > -1 && this.edit.role == 'Approver')
    }
  },
  methods: {
    // vuex
    ...mapActions('app', ['setMainTitle']),

    // action
    startAddFinanceApprover () {
      this.clearEditProps()
      this.edit.typeText = 'Add'
      this.edit.tableCode = 'fin'
      this.edit.tableName = 'Finance FP&A Approver'
      this.edit.roles = ['Supporter', 'Viewer']
      this.isEditingApprover = true
    },
    startAddLegalApprover () {
      this.clearEditProps()
      this.edit.typeText = 'Add'
      this.edit.tableCode = 'lgl'
      this.edit.tableName = 'Legal Approver'
      this.edit.roles = ['Supporter', 'Viewer']
      this.isEditingApprover = true
    },
    startAddFinanceLeaderApprover () {
      this.clearEditProps()
      this.edit.typeText = 'Add'
      this.edit.tableCode = 'fin_ldr'
      this.edit.tableName = 'Finance Leader Approver'
      this.edit.roles = ['Supporter']
      this.isEditingApprover = true
    },
    startAddCpgLeaderApprover () {
      this.clearEditProps()
      this.edit.typeText = 'Add'
      this.edit.tableCode = 'cpg_ldr'
      this.edit.tableName = 'CPG Leader Approver'
      this.edit.roles = ['Supporter']
      this.isEditingApprover = true
    },
    startAddCiLeaderApprover () {
      this.clearEditProps()
      this.edit.typeText = 'Add'
      this.edit.tableCode = 'ci_ldr'
      this.edit.tableName = 'CI Leader Approver'
      this.edit.roles = ['Supporter']
      this.isEditingApprover = true
    },
    startEditFinanceApprover (item) {
      this.clearEditProps()
      this.edit.typeText = 'Edit'
      this.edit.tableCode = 'fin'
      this.edit.tableName = 'Finance FP&A Approver'
      this.edit.roles = ['Supporter', 'Viewer']
      this.edit.role = item.role
      this.edit.location = item.location
      this.edit.notified = item.notified
      this.edit.target.email = item.user.email
      this.edit.target.name = item.user.name
      this.edit.target.icon = item.user.icon
      this.isEditingApprover = true
    },
    startEditLegalApprover (item) {
      this.clearEditProps()
      this.edit.typeText = 'Edit'
      this.edit.tableCode = 'lgl'
      this.edit.tableName = 'Legal FP&A Approver'
      this.edit.roles = ['Supporter', 'Viewer']
      this.edit.role = item.role
      this.edit.location = item.location
      this.edit.notified = item.notified
      this.edit.target.email = item.user.email
      this.edit.target.name = item.user.name
      this.edit.target.icon = item.user.icon
      this.isEditingApprover = true
    },
    startEditFinanceLeaderApprover (item) {
      this.clearEditProps()
      this.edit.typeText = 'Edit'
      this.edit.tableCode = 'fin_ldr'
      this.edit.tableName = 'Finance Leader Approver'
      this.edit.roles = ['Supporter']
      this.edit.role = item.role
      this.edit.notified = item.notified
      this.edit.target.email = item.user.email
      this.edit.target.name = item.user.name
      this.edit.target.icon = item.user.icon
      this.isEditingApprover = true
    },
    startEditCpgLeaderApprover (item) {
      this.clearEditProps()
      this.edit.typeText = 'Edit'
      this.edit.tableCode = 'cpg_ldr'
      this.edit.tableName = 'CPG Leader Approver'
      this.edit.roles = ['Supporter']
      this.edit.role = item.role
      this.edit.notified = item.notified
      this.edit.target.email = item.user.email
      this.edit.target.name = item.user.name
      this.edit.target.icon = item.user.icon
      this.isEditingApprover = true
    },
    startEditCiLeaderApprover (item) {
      this.clearEditProps()
      this.edit.typeText = 'Edit'
      this.edit.tableCode = 'ci_ldr'
      this.edit.tableName = 'CI Leader Approver'
      this.edit.roles = ['Supporter']
      this.edit.role = item.role
      this.edit.notified = item.notified
      this.edit.target.email = item.user.email
      this.edit.target.name = item.user.name
      this.edit.target.icon = item.user.icon
      this.isEditingApprover = true
    },
    startRemoveFinanceApprover (item) {
      this.clearEditProps()
      this.edit.tableCode = 'fin'
      this.edit.tableName = 'Finance FP&A Approver'
      this.edit.role = item.role
      this.edit.target.email = item.user.email
      this.edit.target.name = item.user.name
      this.edit.target.icon = item.user.icon
      this.isRemovingApprover = true
    },
    startRemoveLegalApprover (item) {
      this.clearEditProps()
      this.edit.tableCode = 'lgl'
      this.edit.tableName = 'Legal Approver'
      this.edit.role = item.role
      this.edit.target.email = item.user.email
      this.edit.target.name = item.user.name
      this.edit.target.icon = item.user.icon
      this.isRemovingApprover = true
    },
    startRemoveFinanceLeaderApprover (item) {
      this.clearEditProps()
      this.edit.tableCode = 'fin_ldr'
      this.edit.tableName = 'Finance Leader Approver'
      this.edit.role = item.role
      this.edit.target.email = item.user.email
      this.edit.target.name = item.user.name
      this.edit.target.icon = item.user.icon
      this.isRemovingApprover = true
    },
    startRemoveCpgLeaderApprover (item) {
      this.clearEditProps()
      this.edit.tableCode = 'cpg_ldr'
      this.edit.tableName = 'CPG Leader Approver'
      this.edit.role = item.role
      this.edit.target.email = item.user.email
      this.edit.target.name = item.user.name
      this.edit.target.icon = item.user.icon
      this.isRemovingApprover = true
    },
    startRemoveCiLeaderApprover (item) {
      this.clearEditProps()
      this.edit.tableCode = 'ci_ldr'
      this.edit.tableName = 'CI Leader Approver'
      this.edit.role = item.role
      this.edit.target.email = item.user.email
      this.edit.target.name = item.user.name
      this.edit.target.icon = item.user.icon
      this.isRemovingApprover = true
    },
    async doRemove () {
      if (!this.edit.tableCode) return

      let snackbarParam = { type: 'error', text: 'Fail to remove' }
      this.isRemovingApprover = false
      this.$eventBus.$emit('snackbar', { type: 'loading', text: 'Removing Approver' })
      try {
        const res = await xPost(
          'admin',
          'rem_contr_rev_' + this.edit.tableCode,
          {
            loginStatus: this.loginStatus,
            email: this.edit.target.email,
            location: this.edit.location
          }
        )
        if (res.data.status === 200) {
          snackbarParam = { type: 'success', text: 'Approver Removed' }
        } else {
          try {
            if (res.data.message === 'Authorize Failed') {
              this.$router.push('/login')
              return []
            }
          } catch (e) {}
          throw ''
        }
      } catch (err) {
        console.log('Error [remove approver]')
        // console.log(err)
      }
      await this.refreshAllTables()
      this.$eventBus.$emit('snackbar', snackbarParam)
      this.clearEditProps()
    },
    // working
    async saveEdit (item) {
      if (!this.edit.tableCode) return

      let index_email = -1
      switch (this.edit.tableCode) {
        case 'fin':
          index_email = this.financeApprovers.findIndex(el => el.user.email == item.target.email)
          break;
      
        case 'lgl':
          index_email = this.legalApprovers.findIndex(el => el.user.email == item.target.email)
          break;
      
        case 'fin_ldr':
          index_email = this.financeLeaderApprovers.findIndex(el => el.user.email == item.target.email)
          break;
      
        case 'cpg_ldr':
          index_email = this.cpgLeaderApprovers.findIndex(el => el.user.email == item.target.email)
          break;
      
        case 'ci_ldr':
          index_email = this.ciLeaderApprovers.findIndex(el => el.user.email == item.target.email)
          break;
      
        default:
          break;
      }

      if ((this.edit.target.email != item.target.email) && index_email > -1) {
        this.$eventBus.$emit('snackbar', { type: 'warning', text: 'Approver already exists' })
        return
      }

      let snackbarParam = { type: 'error', text: 'Fail to save' }
      this.$eventBus.$emit('snackbar', { type: 'loading', text: 'Saving' })
      try {
        const res = await xPost(
          'admin',
          'edit_contr_rev_' + this.edit.tableCode,
          {
            loginStatus: this.loginStatus,
            email: this.edit.target.email,
            new_email: item.target.email,
            new_name: item.target.name,
            location: this.edit.location,
            role: this.edit.role,
            new_role: item.role,
            notified: item.notified
          }
        )
        if (res.data.status === 200) {
          snackbarParam = { type: 'success', text: 'Approver Saved' }
          await this.refreshAllTables()
          this.isEditingApprover = false
          this.clearEditProps()
        } else {
          try {
            if (res.data.message === 'Authorize Failed') {
              this.$router.push('/login')
              return []
            }
          } catch (e) {}
          throw ''
        }
      } catch (err) {
        console.log('Error [remove add/edit]')
        // console.log(err)
      }
      this.$eventBus.$emit('snackbar', snackbarParam)
    },

    // axios
    async getFinanceApprover(loading) {
      loading = loading || false
      try {
        if (loading) this.isFinanceApproverLoading = true
        const res = await xPost(
            'admin',
            'getcontrrevfinapprover',
            {
              loginStatus: this.loginStatus
            }
          )
          if (res.data.status === 200 && res.data.data) {
            const data = res.data.data
            this.financeApprovers = data.map(el => ({
              role: el.Role,
              location: el.Location,
              user: {
                email: el.Email,
                name: el.Name,
                icon: el.Icon
              },
              notified: el.Notified,
              deletable: el.Role != 'Approver',
              key: el.Email
            }))
          } else {
            try {
              if (res.data.message === 'Authorize Failed') {
                this.$router.push('/login')
              } else if (res.data.detail === "No Admin") {
                this.$router.push('/')
              }
            } catch (e) {}
            throw ''
          }
      } catch (err) {
        console.log('Error [get finance approvers]')
        // console.log(err)
      }
      this.isFinanceApproverLoading = false
    },
    async getLegalApprover(loading) {
      loading = loading || false
      try {
        if (loading) this.isLegalApproverLoading = true
        const res = await xPost(
            'admin',
            'getcontrrevlglapprover',
            {
              loginStatus: this.loginStatus
            }
          )
          if (res.data.status === 200 && res.data.data) {
            const data = res.data.data
            this.legalApprovers = data.map(el => ({
              role: el.Role,
              location: el.Location,
              user: {
                email: el.Email,
                name: el.Name,
                icon: el.Icon
              },
              notified: el.Notified,
              deletable: el.Role != 'Approver',
              key: el.Email
            }))
          } else {
            try {
              if (res.data.message === 'Authorize Failed') {
                this.$router.push('/login')
              } else if (res.data.detail === "No Admin") {
                this.$router.push('/')
              }
            } catch (e) {}
            throw ''
          }
      } catch (err) {
        console.log('Error [get legal approvers]')
        // console.log(err)
      }
      this.isLegalApproverLoading = false
    },
    async getFinanceLeaderApprover(loading) {
      loading = loading || false
      try {
        if (loading) this.isFinanceLeaderApproverLoading = true
        const res = await xPost(
            'admin',
            'getcontrrevfinldrapprover',
            {
              loginStatus: this.loginStatus
            }
          )
          if (res.data.status === 200 && res.data.data) {
            const data = res.data.data
            this.financeLeaderApprovers = data.map(el => ({
              role: el.Role,
              user: {
                email: el.Email,
                name: el.Name,
                icon: el.Icon
              },
              notified: el.Notified,
              deletable: el.Role != 'Approver',
              key: el.Email
            }))
          } else {
            try {
              if (res.data.message === 'Authorize Failed') {
                this.$router.push('/login')
              } else if (res.data.detail === "No Admin") {
                this.$router.push('/')
              }
            } catch (e) {}
            throw ''
          }
      } catch (err) {
        console.log('Error [get finance leader approvers]')
        // console.log(err)
      }
      this.isFinanceLeaderApproverLoading = false
    },
    async getCpgLeaderApprover(loading) {
      loading = loading || false
      try {
        if (loading) this.isCpgLeaderApproverLoading = true
        const res = await xPost(
            'admin',
            'getcontrrevcpgldrapprover',
            {
              loginStatus: this.loginStatus
            }
          )
          if (res.data.status === 200 && res.data.data) {
            const data = res.data.data
            this.cpgLeaderApprovers = data.map(el => ({
              role: el.Role,
              user: {
                email: el.Email,
                name: el.Name,
                icon: el.Icon
              },
              notified: el.Notified,
              deletable: el.Role != 'Approver',
              key: el.Email
            }))
          } else {
            try {
              if (res.data.message === 'Authorize Failed') {
                this.$router.push('/login')
              } else if (res.data.detail === "No Admin") {
                this.$router.push('/')
              }
            } catch (e) {}
            throw ''
          }
      } catch (err) {
        console.log('Error [get cpg leader approvers]')
        // console.log(err)
      }
      this.isCpgLeaderApproverLoading = false
    },
    async getCiLeaderApprover(loading) {
      loading = loading || false
      try {
        if (loading) this.isCiLeaderApproverLoading = true
        const res = await xPost(
            'admin',
            'getcontrrevcildrapprover',
            {
              loginStatus: this.loginStatus
            }
          )
          if (res.data.status === 200 && res.data.data) {
            const data = res.data.data
            this.ciLeaderApprovers = data.map(el => ({
              role: el.Role,
              user: {
                email: el.Email,
                name: el.Name,
                icon: el.Icon
              },
              notified: el.Notified,
              deletable: el.Role != 'Approver',
              key: el.Email
            }))
          } else {
            try {
              if (res.data.message === 'Authorize Failed') {
                this.$router.push('/login')
              } else if (res.data.detail === "No Admin") {
                this.$router.push('/')
              }
            } catch (e) {}
            throw ''
          }
      } catch (err) {
        console.log('Error [get ci leader approvers]')
        // console.log(err)
      }
      this.isCiLeaderApproverLoading = false
    },
    // working

    // function
    async refreshAllTables() {
      const gfa = this.getFinanceApprover()
      const gla = this.getLegalApprover()
      const gfla = this.getFinanceLeaderApprover()
      const gcpla = this.getCpgLeaderApprover()
      const gcila = this.getCiLeaderApprover()
      const res_gfa = await gfa
      const res_gla = await gla
      const res_gfla = await gfla
      const res_gcpla = await gcpla
      const res_gcila = await gcila
    },
    clearEditProps() {
      this.edit.typeText = null
      this.edit.tableCode = null
      this.edit.tableName = null
      this.edit.location = null
      this.edit.roles = []
      this.edit.role = null
      this.edit.notified = null
      this.edit.target.email = null
      this.edit.target.name = null
      this.edit.target.icon = null
    },

    // init
    async init() {
      await this.refreshAllTables()
    }
  },
  async created () {
    this.setMainTitle('Client Project Contract Admin Setting')
    let init = await this.init()
    this.isPageLoading = false
  }
}
</script>

<style lang="sass">
  .admin-wbs
    width: 100%

    button
      margin-top: 3px
      margin-bottom: 3px
    

    .admin-wbs--loading
      width: 100%
      text-align: center
      padding-top: calc(50vh - 85px)
    
  
</style>
